import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import {
  ScrollbarStyled,
  ScrollWrap,
  OuterContainer,
  InnerHandle,
  InnerContainer,
  ContentWrapper,
  ScrollHandle,
} from './styles/ScrollbarStyled';

class Scrollbar extends PureComponent {
  constructor(props) {
    super(props);

    this.scrollbarYWidth = 0;
    this.innerContainer = null;
    this.state = {
      scrollPos: 0,
      onDrag: false,
      outerBlock: false,
    };
  }

  componentDidMount() {
    const { scrollTo } = this.props;
    if (this.innerContainer) {
      this.scrollbarYWidth =
        this.innerContainer.offsetWidth - this.innerContainer.clientWidth;
      this.forceUpdate();
    }
    if (typeof scrollTo !== 'undefined') {
      this.updateScrollPosition(scrollTo);
    } else {
      this.forceUpdate();
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { scrollPos } = this.state;

    const prevContentHeight = this.contentHeight;
    const prevVisibleHeight = this.visibleHeight;
    const reachedBottomOnPrevRender =
      prevState.scrollPos >= prevContentHeight - prevVisibleHeight;

    this.contentHeight = this.innerContainer.scrollHeight;
    this.scrollbarYWidth =
      this.innerContainer.offsetWidth - this.innerContainer.clientWidth;
    this.visibleHeight = this.innerContainer.clientHeight;
    this.scrollRatio = this.contentHeight
      ? this.visibleHeight / this.contentHeight
      : 1;
    const reachedBottomOnCurrentRender =
      scrollPos >= this.contentHeight - this.visibleHeight;
    const contentResized = prevContentHeight !== this.contentHeight;

    this.toggleScrollIfNeeded();

    if (this.props.freezePosition || prevProps.freezePosition) {
      this.adjustFreezePosition(prevProps);
    }
    if (
      typeof this.props.scrollTo !== 'undefined' &&
      this.props.scrollTo !== prevProps.scrollTo
    ) {
      this.updateScrollPosition(this.props.scrollTo);
    } else if (
      this.props.keepAtBottom &&
      contentResized &&
      reachedBottomOnPrevRender &&
      !reachedBottomOnCurrentRender
    ) {
      this.updateScrollPosition(this.contentHeight - this.visibleHeight);
    }
  }

  componentWillUnmount() {
    document.removeEventListener('mousemove', this.onHandleDrag);
    document.removeEventListener('mouseup', this.onHandleDragEnd);
  }

  onScroll = (event) => {
    const { onScroll } = this.props;
    const { outerBlock } = this.state;

    if (this.props.freezePosition) {
      return;
    }
    this.adjustCustomScrollPosToContentPos(event.currentTarget.scrollTop);
    if (onScroll) {
      if (outerBlock) {
        this.setState({
          outerBlock: true,
        });
      }
      onScroll(event);
    }
  };

  onMouseDown = (event) => {
    if (!this.hasScroll || !this.isMouseEventOnScrollHandle(event)) {
      return;
    }

    this.startDragHandlePos = this.getScrollHandleStyle().top;
    this.startDragMousePos = event.pageY;
    this.setState({
      onDrag: true,
    });
    document.addEventListener('mousemove', this.onHandleDrag);
    document.addEventListener('mouseup', this.onHandleDragEnd);
  };

  onHandleDrag = (event) => {
    event.preventDefault();
    const mouseDeltaY = event.pageY - this.startDragMousePos;
    const handleTopPosition = this.ensureWithinLimits(
      this.startDragHandlePos + mouseDeltaY,
      0,
      this.visibleHeight - this.scrollHandleHeight
    );
    const newScrollValue =
      this.getScrollValueFromHandlePosition(handleTopPosition);
    this.updateScrollPosition(newScrollValue);
  };

  onHandleDragEnd = (e) => {
    this.setState({
      onDrag: false,
    });
    e.preventDefault();
    document.removeEventListener('mousemove', this.onHandleDrag);
    document.removeEventListener('mouseup', this.onHandleDragEnd);
  };

  onClick = (event) => {
    if (
      !this.hasScroll ||
      !this.isMouseEventOnCustomScrollbar(event) ||
      this.isMouseEventOnScrollHandle(event)
    ) {
      return;
    }
    const newScrollHandleTop = this.calculateNewScrollHandleTop(event);
    const newScrollValue =
      this.getScrollValueFromHandlePosition(newScrollHandleTop);

    this.updateScrollPosition(newScrollValue);
  };

  getOuterContainerStyle = () => ({
    height: this.props.heightRelativeToParent || this.props.flex ? '100%' : '',
  });

  getScrollStyles = () => {
    const { heightRelativeToParent, freezePosition, maxHeight } = this.props;
    const scrollSize = this.scrollbarYWidth || 20;
    const marginKey = this.props.rtl ? 'marginLeft' : 'marginRight';
    const innerContainerStyle = {
      [marginKey]: -1 * scrollSize,
      height: heightRelativeToParent || this.props.flex ? '100%' : '',
      maxHeight: maxHeight || '100%',
    };
    const contentWrapperStyle = {
      [marginKey]: this.scrollbarYWidth ? 0 : scrollSize,
      height: heightRelativeToParent || this.props.flex ? '100%' : '',
      overflowY: freezePosition ? 'hidden' : 'visible',
    };

    return {
      innerContainer: innerContainerStyle,
      contentWrapper: contentWrapperStyle,
    };
  };

  getRootStyles = () => {
    const { flex, heightRelativeToParent } = this.props;
    const result = {};

    if (heightRelativeToParent) {
      result.height = heightRelativeToParent;
    } else if (flex) {
      result.flex = flex;
    }

    return result;
  };

  getScrollValueFromHandlePosition = (handlePosition) =>
    handlePosition / this.scrollRatio;

  getScrollHandleStyle = () => {
    const handlePosition = this.state.scrollPos * this.scrollRatio;
    this.scrollHandleHeight = this.visibleHeight * this.scrollRatio;
    return {
      height: this.scrollHandleHeight,
      top: handlePosition,
    };
  };

  setScrollTop = (target, value) => {
    if (target) target.scrollTop = value;
  };

  calculateNewScrollHandleTop = (clickEvent) => {
    const domNode = this.node;
    const boundingRect = domNode.getBoundingClientRect();
    const currentTop = boundingRect.top + window.pageYOffset;
    const clickYRelativeToScrollbar = clickEvent.pageY - currentTop;
    const scrollHandleTop = this.getScrollHandleStyle().top;
    let newScrollHandleTop;
    const isBelowHandle =
      clickYRelativeToScrollbar > scrollHandleTop + this.scrollHandleHeight;
    if (isBelowHandle) {
      newScrollHandleTop =
        scrollHandleTop +
        Math.min(
          this.scrollHandleHeight,
          this.visibleHeight - this.scrollHandleHeight
        );
    } else {
      newScrollHandleTop =
        scrollHandleTop - Math.max(this.scrollHandleHeight, 0);
    }
    return newScrollHandleTop;
  };

  isMouseEventOnScrollHandle = (event) => {
    const scrollHandle = this.scrollHandle;
    return this.isEventPosOnDomNode(event, scrollHandle);
  };

  isMouseEventOnCustomScrollbar = (event) => {
    const customScrollbar = this.customScrollbar;
    return this.isEventPosOnDomNode(event, customScrollbar);
  };

  updateScrollPosition = (scrollValue) => {
    this.setScrollTop(this.innerContainer, scrollValue);
    this.setState({
      scrollPos: scrollValue,
    });
  };

  blockOuterScroll = (e) => {
    const { outerBlock } = this.state;

    if (outerBlock) {
      const contentNode = e.currentTarget;
      const totalHeight = e.currentTarget.scrollHeight;
      const maxScroll = totalHeight - e.currentTarget.offsetHeight;
      const delta = e.deltaY % 3 ? e.deltaY : e.deltaY * 10;
      if (contentNode.scrollTop + delta <= 0) {
        this.setScrollTop(contentNode, 0);
        e.preventDefault();
      } else if (contentNode.scrollTop + delta >= maxScroll) {
        this.setScrollTop(contentNode, maxScroll);
        e.preventDefault();
      }
      e.stopPropagation();
    }
  };

  adjustCustomScrollPosToContentPos = (scrollPosition) => {
    this.setState({
      scrollPos: scrollPosition,
    });
  };

  toggleScrollIfNeeded = () => {
    const shouldHaveScroll = this.contentHeight - this.visibleHeight > 1;
    if (this.hasScroll !== shouldHaveScroll) {
      this.hasScroll = shouldHaveScroll;
      this.forceUpdate();
    }
  };

  adjustFreezePosition = (prevProps) => {
    const { freezePosition } = this.props;
    const { scrollPos } = this.state;
    const contentWrapper = this.contentWrapper;

    if (freezePosition) {
      this.setScrollTop(contentWrapper, scrollPos);
    }

    if (prevProps.freezePosition) {
      this.setScrollTop(this.innerContainer, scrollPos);
    }
  };

  ensureWithinLimits = (value, min, max) => {
    min = !min && min !== 0 ? value : min;
    max = !max && max !== 0 ? value : max;
    if (min > max) {
      return value;
    }
    if (value < min) {
      return min;
    }
    if (value > max) {
      return max;
    }
    return value;
  };

  isEventPosOnDomNode = (event, domNode) => {
    if (domNode) {
      const nodeClientRect = domNode.getBoundingClientRect();
      return (
        event.clientX > nodeClientRect.left &&
        event.clientX < nodeClientRect.left + nodeClientRect.width &&
        event.clientY > nodeClientRect.top &&
        event.clientY < nodeClientRect.top + nodeClientRect.height
      );
    }
    return false;
  };

  enforceMinHandleHeight = (calculatedStyle) => {
    const minHeight = this.props.minScrollHandleHeight;
    if (calculatedStyle.height >= minHeight) {
      return calculatedStyle;
    }

    const diffHeightBetweenMinAndCalculated =
      minHeight - calculatedStyle.height;
    const scrollPositionToAvailableScrollRatio =
      this.state.scrollPos / (this.contentHeight - this.visibleHeight);
    const scrollHandlePosAdjustmentForMinHeight =
      diffHeightBetweenMinAndCalculated * scrollPositionToAvailableScrollRatio;
    const handlePosition =
      calculatedStyle.top - scrollHandlePosAdjustmentForMinHeight;

    return {
      height: minHeight,
      top: handlePosition,
    };
  };

  assignCustomScrollRef = (ref) => {
    this.customScrollbar = ref;
  };

  assignScrollHandleRef = (ref) => {
    this.scrollHandle = ref;
  };

  assignInnerContainerRef = (ref) => {
    this.innerContainer = ref;
  };

  assignContentWrapperRef = (ref) => {
    this.contentWrapper = ref;
  };

  render() {
    const { rtl, children, type } = this.props;
    const { onDrag } = this.state;
    const scrollStyles = this.getScrollStyles();
    const rootStyle = this.getRootStyles();
    const scrollHandleStyle = this.enforceMinHandleHeight.call(
      this,
      this.getScrollHandleStyle()
    );

    return (
      <ScrollbarStyled dragState={onDrag} style={rootStyle}>
        <OuterContainer
          style={this.getOuterContainerStyle()}
          onMouseDown={this.onMouseDown}
          onClick={this.onClick}
        >
          {this.hasScroll ? (
            <ScrollWrap
              ref={this.assignCustomScrollRef}
              dragState={onDrag}
              rtl={rtl}
              key="scrollbar"
            >
              <ScrollHandle
                ref={this.assignScrollHandleRef}
                style={scrollHandleStyle}
              >
                <InnerHandle type={type} dragState={onDrag} />
              </ScrollHandle>
            </ScrollWrap>
          ) : null}
          <InnerContainer
            ref={this.assignInnerContainerRef}
            id={'innerScrollContainer'}
            style={scrollStyles.innerContainer}
            onScroll={this.onScroll}
            onWheel={this.blockOuterScroll}
          >
            <ContentWrapper
              ref={this.assignContentWrapperRef}
              style={scrollStyles.contentWrapper}
            >
              {children}
            </ContentWrapper>
          </InnerContainer>
        </OuterContainer>
      </ScrollbarStyled>
    );
  }
}

Scrollbar.propTypes = {
  children: PropTypes.any,
  heightRelativeToParent: PropTypes.string,
  minScrollHandleHeight: PropTypes.number,
  onScroll: PropTypes.func,
  freezePosition: PropTypes.bool,
  flex: PropTypes.string,
  rtl: PropTypes.bool,
  scrollTo: PropTypes.number,
  keepAtBottom: PropTypes.bool,
  type: PropTypes.string,
  maxHeight: PropTypes.string,
};

Scrollbar.defaultProps = {
  minScrollHandleHeight: 38,
  keepAtBottom: false,
  scrollTo: 0,
  rtl: false,
  flex: '',
  freezePosition: false,
  addScrolledClass: false,
  onScroll: Function.prototype,
  heightRelativeToParent: '',
  children: '',
  type: null,
  maxHeight: null,
};

export default Scrollbar;
